/**
 * Unified Conversation Sidebar Component
 * 
 * Displays user conversations/sessions and allows loading messages from selected sessions.
 * Shared conversation sidebar used by Smart Insights.
 */

import { useState, useEffect, useCallback, useRef, type UIEvent } from 'react';
import {
  MessageSquare,
  MessageSquareText,
  Clock,
  History,
  X,
  Search,
  Plus,
  Trash2,
  Download,
  Star,
  MoreHorizontal,
  Pencil,
  Check,
  ArrowRight,
} from 'lucide-react';
import { ConversationService, Conversation, ConversationMessage, ConversationBookmarkResponse } from '../../services/conversationService';
import { logInfo, logError } from '../../utils/logger';
import { parseMessageContent } from '../../utils/messageParser';
import { useGlobalToast } from '@/components/UI/toaster';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/UI/tooltip';
import { cn } from '@/lib/utils';

const FINANCE_V2_THREAD_PAGE_SIZE = 25;

interface ConversationSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectConversation: (conversationId: string, messages: ConversationMessage[], meta?: { dataSourceName?: string }) => void;
  onStartNewConversation?: () => void;  // Callback to start a new conversation
  currentConversationId?: string;
  variant?: 'default' | 'financeV2';
}

export function ConversationSidebar({
  isOpen,
  onClose,
  onSelectConversation,
  onStartNewConversation,
  currentConversationId,
  variant = 'default'
}: ConversationSidebarProps) {
  const isFinanceV2Variant = variant === 'financeV2';
  // Normalize timestamp values coming from CI/backends.
  // CI history may provide epoch seconds (e.g., 1769241351.57) which JS Date treats as milliseconds -> 1970.
  const normalizeTimestampToIso = (ts: any): string => {
    if (ts == null) return new Date().toISOString();

    // Numbers: seconds or milliseconds
    if (typeof ts === 'number') {
      const ms = ts < 946684800000 ? Math.round(ts * 1000) : Math.round(ts); // <2000-01-01 in ms => seconds
      return new Date(ms).toISOString();
    }

    if (typeof ts === 'string') {
      const trimmed = ts.trim();
      // Numeric string: seconds or milliseconds
      if (/^\d+(\.\d+)?$/.test(trimmed)) {
        const num = Number(trimmed);
        const ms = num < 946684800000 ? Math.round(num * 1000) : Math.round(num);
        return new Date(ms).toISOString();
      }
      // ISO string
      const d = new Date(trimmed);
      if (!Number.isNaN(d.getTime())) return d.toISOString();
    }

    return new Date().toISOString();
  };
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [submittedSearchQuery, setSubmittedSearchQuery] = useState('');
  const [hasMoreConversations, setHasMoreConversations] = useState(false);
  const [nextConversationOffset, setNextConversationOffset] = useState<number | null>(null);
  const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(currentConversationId ?? null);
  const [loadingMessages, setLoadingMessages] = useState<string | null>(null);
  const [bookmarksResponse, setBookmarksResponse] = useState<ConversationBookmarkResponse | null>(null);
  const [savingBookmarkConversationId, setSavingBookmarkConversationId] = useState<string | null>(null);
  const [deletingConversationId, setDeletingConversationId] = useState<string | null>(null);
  const [exportingConversationId, setExportingConversationId] = useState<string | null>(null);
  const [openActionsConversationId, setOpenActionsConversationId] = useState<string | null>(null);
  const [actionsMenuPlacement, setActionsMenuPlacement] = useState<'up' | 'down'>('down');
  const [editingConversationId, setEditingConversationId] = useState<string | null>(null);
  const [editingConversationTitle, setEditingConversationTitle] = useState('');
  const [renamingConversationId, setRenamingConversationId] = useState<string | null>(null);
  const renameInputRef = useRef<HTMLInputElement | null>(null);
  const loadingMoreConversationsRef = useRef(false);
  const { success: toastSuccess, error: toastError } = useGlobalToast();

  const loadConversations = useCallback(async () => {
    setLoading(true);
    loadingMoreConversationsRef.current = false;
    setLoadingMoreConversations(false);
    setError(null);
    
    try {
      const response = await ConversationService.getUserConversations(
        isFinanceV2Variant ? submittedSearchQuery : undefined,
        isFinanceV2Variant ? { limit: FINANCE_V2_THREAD_PAGE_SIZE, offset: 0 } : undefined
      );
      
      logInfo('ConversationSidebar', 'API Response:', {
        success: response.success,
        conversationsCount: response.conversations?.length || 0,
        hasError: !!response.error,
        error: response.error
      });

      if (response.success && response.conversations) {
        logInfo('ConversationSidebar', `Received ${response.conversations.length} conversations from API`);
        
        // Sort conversations by date (most recent first)
        // Normalize timestamps: API may return epoch seconds (e.g. 1771914218.51) or ms
        const toMs = (v: any): number => {
          if (v == null) return 0;
          const n = typeof v === 'number' ? v : typeof v === 'string' && /^\d+(\.\d+)?$/.test(v.trim()) ? Number(v) : new Date(v).getTime();
          if (typeof n !== 'number' || Number.isNaN(n)) return 0;
          return n < 946684800000 ? Math.round(n * 1000) : n; // < 2000-01-01 in ms => treat as seconds
        };
        const sorted = isFinanceV2Variant ? response.conversations : [...response.conversations].sort((a, b) => {
          const getRaw = (conv: any) =>
            conv.endedAt ?? conv.endTime ?? conv.end_time ??
            conv.updatedAt ?? conv.updated_at ?? conv.lastUpdated ??
            conv.startedAt ?? conv.startTime ?? conv.start_time ??
            conv.createdAt ?? conv.created_at ?? conv.created ??
            null;
          const dateA = toMs(getRaw(a));
          const dateB = toMs(getRaw(b));
          return dateB - dateA;
        });

        setConversations(sorted);
        if (isFinanceV2Variant) {
          setHasMoreConversations(Boolean(response.hasMore));
          setNextConversationOffset(response.nextOffset ?? null);
        } else {
          setHasMoreConversations(false);
          setNextConversationOffset(null);
        }
        logInfo('ConversationSidebar', `Loaded ${sorted.length} conversations`, { count: sorted.length });

        // Debug: Log first conversation to see available fields (only in development)
        if (sorted.length > 0 && import.meta.env.DEV) {
          const firstConv = sorted[0] as any;
          logInfo('ConversationSidebar', 'Sample conversation fields:', {
            conversationId: firstConv.conversationId,
            startedAt: firstConv.startedAt,
            allFields: Object.keys(firstConv),
          });
        }
      } else {
        setError(response.error || 'Failed to load conversations');
        setConversations([]);
        setHasMoreConversations(false);
        setNextConversationOffset(null);
      }
    } catch (err: any) {
      logError('ConversationSidebar', 'Failed to load conversations', err);
      setError(err.message || 'Failed to load conversations');
      setConversations([]);
      setHasMoreConversations(false);
      setNextConversationOffset(null);
    } finally {
      setLoading(false);
    }
  }, [isFinanceV2Variant, submittedSearchQuery]);

  const loadMoreConversations = useCallback(async () => {
    if (
      !isFinanceV2Variant ||
      loading ||
      loadingMoreConversationsRef.current ||
      !hasMoreConversations ||
      nextConversationOffset === null
    ) {
      return;
    }

    loadingMoreConversationsRef.current = true;
    setLoadingMoreConversations(true);
    try {
      const response = await ConversationService.getUserConversations(
        submittedSearchQuery,
        { limit: FINANCE_V2_THREAD_PAGE_SIZE, offset: nextConversationOffset }
      );

      if (response.success && response.conversations) {
        setConversations((prev) => {
          const existingIds = new Set(prev.map((conversation) => conversation.conversationId));
          const additions = response.conversations.filter((conversation) => !existingIds.has(conversation.conversationId));
          return [...prev, ...additions];
        });
        setHasMoreConversations(Boolean(response.hasMore));
        setNextConversationOffset(response.nextOffset ?? null);
      } else {
        toastError('Could not load more', response.error ?? 'Failed to load more conversations.');
      }
    } catch (err) {
      logError('ConversationSidebar', 'Failed to load more conversations', err instanceof Error ? err : new Error('Unknown error'));
      toastError('Could not load more', 'Failed to load more conversations. Please try again.');
    } finally {
      loadingMoreConversationsRef.current = false;
      setLoadingMoreConversations(false);
    }
  }, [
    hasMoreConversations,
    isFinanceV2Variant,
    loading,
    nextConversationOffset,
    submittedSearchQuery,
    toastError,
  ]);

  const handleConversationScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
    if (!isFinanceV2Variant) return;
    if (openActionsConversationId) {
      setOpenActionsConversationId(null);
    }
    const target = event.currentTarget;
    const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (distanceFromBottom <= 160) {
      void loadMoreConversations();
    }
  }, [isFinanceV2Variant, loadMoreConversations, openActionsConversationId]);

  // Load conversations on mount or when sidebar opens.
  useEffect(() => {
    if (isOpen) {
      loadConversations();
      ConversationService.getConversationBookmarks()
        .then(setBookmarksResponse)
        .catch((err) => {
          console.error('[ConversationSidebar] Failed to load conversation bookmarks', err);
        });
    }
  }, [isOpen, loadConversations]);

  useEffect(() => {
    setSelectedConversationId(currentConversationId ?? null);
  }, [currentConversationId]);

  useEffect(() => {
    if (!editingConversationId) return;
    const input = renameInputRef.current;
    if (!input) return;

    requestAnimationFrame(() => {
      input.focus();
      input.select();
      input.scrollLeft = 0;
    });
  }, [editingConversationId]);

  const handleConversationClick = async (conversation: Conversation) => {
    if (!conversation.conversationId) {
      logError('ConversationSidebar', 'Conversation ID is missing', new Error('Invalid conversation'));
      return;
    }

    setSelectedConversationId(conversation.conversationId);
    setOpenActionsConversationId(null);
    setLoadingMessages(conversation.conversationId);

    try {
      logInfo('ConversationSidebar', `Loading messages for conversation: ${conversation.conversationId}`);
      
      const response = await ConversationService.getConversationMessages(conversation.conversationId);
      
      if (response.success && response.messages) {
        // Log raw response from backend for debugging
        console.log('[ConversationSidebar] Raw backend response:', {
          messageCount: response.messages.length,
          messages: response.messages.map((m: any, i: number) => ({
            index: i,
            sender: m.sender,
            role: m.role,
            hasMessage: !!m.message,
            hasContent: !!m.content,
            messageType: typeof m.message,
            contentType: typeof m.content,
            messagePreview: typeof m.message === 'string' ? m.message.substring(0, 100) : (typeof m.message === 'object' ? 'object' : m.message),
            contentPreview: typeof m.content === 'string' ? m.content.substring(0, 100) : (typeof m.content === 'object' ? 'object' : m.content),
            messageStartsWithBrace: typeof m.message === 'string' ? m.message.trim().startsWith('{') : false,
            allFields: Object.keys(m),
          })),
        });
        
        // Transform messages to match chat interface format
        // Backend returns: sender (HUMAN/BOT), message, timestamp, generatedContext, ragContext
        // Backend now also includes: humanMessageId (for bot messages - links to corresponding user message)
        // Frontend expects: messageId, role (user/assistant), content, timestamp, senderType, humanMessageId
        const transformedMessages: ConversationMessage[] = response.messages.map((msg: any, idx: number) => {
          // Get role FIRST - convert sender (HUMAN/BOT) to role (user/assistant)
          // Note: Backend uses 'sender' for conversation messages, not 'senderType'
          const sender = msg.sender || msg.senderType;
          let role: 'user' | 'assistant' | 'system' = 'user';
          if (sender === 'BOT' || msg.role === 'assistant') {
            role = 'assistant';
          } else if (sender === 'HUMAN' || msg.role === 'user') {
            role = 'user';
          } else if (msg.role === 'system') {
            role = 'system';
          }
          
          // Extract humanMessageId for bot messages (used for linking to user query)
          // Backend adds this from replyToMessageId field
          const humanMessageId = msg.humanMessageId || msg.replyToMessageId || null;
          
          // Get content - backend unwrapBotResponses() behavior:
          // 1. For bot messages: Parses JSON from 'content' or 'message' field and puts unwrapped object in 'content'
          // 2. Original 'message' field is preserved (contains raw JSON string for bot, plain text for user)
          // 3. After unwrapping: 'content' = unwrapped object, 'message' = original JSON string or text
          // 
          // IMPORTANT: For bot messages, we need the RAW JSON string from 'message' field
          // The 'message' field contains the original JSON: {"type":"complete","data":{...}}
          // The 'content' field might be unwrapped object OR parsed text, so we prefer 'message' for bot messages
          let rawContent: any;
          if (role === 'assistant') {
            // Bot message: ALWAYS check 'message' field first (contains raw JSON string)
            // This is the original JSON that was stored, before any parsing
            if (msg.message && typeof msg.message === 'string') {
              const trimmedMessage = msg.message.trim();
              // Check if message looks like JSON (starts with {)
              if (trimmedMessage.startsWith('{') || trimmedMessage.startsWith('[')) {
                // This is the raw JSON string - use it
                rawContent = msg.message;
                logInfo('ConversationSidebar', 'Using raw JSON from message field for bot message');
              } else {
                // Message is not JSON (might be parsed text), try content field
                if (msg.content && typeof msg.content === 'object') {
                  rawContent = msg.content;
                  logInfo('ConversationSidebar', 'Message field is not JSON, using content object');
                } else {
                  rawContent = msg.content || msg.message || '';
                  logInfo('ConversationSidebar', 'Using fallback content for bot message');
                }
              }
            } else if (msg.content && typeof msg.content === 'object') {
              // Content is unwrapped object - use it (backend already unwrapped)
              rawContent = msg.content;
              logInfo('ConversationSidebar', 'Using unwrapped object from content field');
            } else {
              // Fallback
              rawContent = msg.content || msg.message || '';
              logInfo('ConversationSidebar', 'Using fallback for bot message');
            }
          } else {
            // User message: use message field (plain text)
            rawContent = msg.message || msg.content || '';
          }
          
          // Log what we're using for debugging
          if (role === 'assistant') {
            logInfo('ConversationSidebar', 'Bot message rawContent:', {
              type: typeof rawContent,
              isString: typeof rawContent === 'string',
              isObject: typeof rawContent === 'object',
              preview: typeof rawContent === 'string' ? rawContent.substring(0, 100) : 'object',
              startsWithBrace: typeof rawContent === 'string' ? rawContent.trim().startsWith('{') : false,
            });
          }
          
          // Extract botMessageId for ALL messages (both user and bot) for feedback support
          // Priority:
          // 1. botMessageId from backend response (backend extracts it from unwrapped content)
          // 2. botMessageId from unwrapped content object
          // 3. botMessageId from parsed JSON string content
          let botMessageIdFromContent: string | null = null;
          
          // First check if backend already extracted botMessageId and added it to the message
          if (msg.botMessageId || (msg as any).bot_message_id) {
            botMessageIdFromContent = (msg.botMessageId || (msg as any).bot_message_id).toString();
            logInfo('ConversationSidebar', 'Using botMessageId from backend response', {
              botMessageId: botMessageIdFromContent
            });
          } else {
            // Fallback: Extract from content if backend didn't provide it
            // Check if content is already an unwrapped object (from backend unwrapping)
            if (typeof rawContent === 'object' && rawContent !== null) {
              // Backend wraps bot responses as: {"type":"complete","data":{...}, "botMessageId": "..."}
              // Or older assistant payloads: {"response":"...", "botMessageId": "..."}
              // Extract botMessageId from unwrapped content
              botMessageIdFromContent = rawContent.botMessageId || (rawContent as any).bot_message_id || null;
              
              // Also check in data field if content has type/data structure
              if (!botMessageIdFromContent && rawContent.data && typeof rawContent.data === 'object') {
                botMessageIdFromContent = rawContent.data.botMessageId || rawContent.data.bot_message_id || null;
              }
            } else if (typeof rawContent === 'string') {
              // Content is still a JSON string, try to parse it
              try {
                const parsed = JSON.parse(rawContent);
                botMessageIdFromContent = parsed.botMessageId || parsed.bot_message_id || null;
                
                // Also check in data field if parsed has type/data structure
                if (!botMessageIdFromContent && parsed.data && typeof parsed.data === 'object') {
                  botMessageIdFromContent = parsed.data.botMessageId || parsed.data.bot_message_id || null;
                }
              } catch (e) {
                // Not JSON, continue
              }
            }
          }
          
          // Use Communication Infrastructure message ID for ALL messages (user and bot) for feedback support
          // Priority:
          // 1. For bot messages: botMessageId from backend/content (the actual bot message ID)
          // 2. For all messages: messageId from Communication Infrastructure (the message ID in the system)
          // 3. Fallback: Generate synthetic ID if neither is available
          let messageId: string;
          if (role === 'assistant') {
            // For bot messages: prefer botMessageId from backend/content, then messageId from Communication Infrastructure
            if (botMessageIdFromContent) {
              messageId = botMessageIdFromContent;
            } else if (msg.messageId || msg.id) {
              messageId = (msg.messageId || msg.id).toString();
            } else {
              // Deterministic fallback (avoid Date.now collisions -> missing bot messages)
              const iso = normalizeTimestampToIso(msg.timestamp);
              const ms = new Date(iso).getTime();
              messageId = `msg-${ms}-${idx}-ai`;
            }
          } else {
            // For user messages: use messageId from Communication Infrastructure (human message ID)
            if (msg.messageId || msg.id) {
              messageId = (msg.messageId || msg.id).toString();
            } else {
              const iso = normalizeTimestampToIso(msg.timestamp);
              const ms = new Date(iso).getTime();
              messageId = `msg-${ms}-${idx}-user`;
            }
          }
          
          // For bot messages (assistant role), ALWAYS use 'message' field which contains raw JSON
          // The 'message' field has the original JSON string: {"type":"complete","data":{...}}
          // For user messages, use message field as plain text
          let content: string;
          if (role === 'assistant') {
            // Bot message: ALWAYS check 'message' field first (contains raw JSON string)
            if (msg.message && typeof msg.message === 'string') {
              const trimmedMessage = msg.message.trim();
              if (trimmedMessage.startsWith('{') || trimmedMessage.startsWith('[')) {
                // Message field contains raw JSON - use it directly
                content = msg.message;
                logInfo('ConversationSidebar', '✅ Using raw JSON from message field for bot message');
              } else {
                // Message field is not JSON, try content field (might be unwrapped object)
                if (typeof rawContent === 'object' && rawContent !== null) {
                  content = JSON.stringify(rawContent);
                  logInfo('ConversationSidebar', 'Message field not JSON, using content object');
                } else {
                  content = rawContent || msg.message || '';
                  logInfo('ConversationSidebar', 'Using fallback content for bot message');
                }
              }
            } else if (typeof rawContent === 'object' && rawContent !== null) {
              // Content is already an object - stringify it to preserve structure
              content = JSON.stringify(rawContent);
              logInfo('ConversationSidebar', 'Using content object, stringified');
            } else {
              content = rawContent || msg.message || '';
              logInfo('ConversationSidebar', 'Using fallback for bot message');
            }
          } else {
            // For user messages, use message field as plain text (no JSON parsing)
            content = msg.message || rawContent || '';
          }
          
          // Also preserve the original message field for bot messages.
          const result: ConversationMessage = {
            messageId, // This ID will be used for feedback submission in all bots
            role,
            content,
            timestamp: normalizeTimestampToIso(msg.timestamp),
            senderType: sender,
          };
          
          // For bot messages: set both botMessageId (for feedback) and humanMessageId (for linking)
          if (role === 'assistant') {
            // Set botMessageId for feedback submission - prefer ID from content, fall back to messageId
            result.botMessageId = botMessageIdFromContent || messageId;

            // Set humanMessageId for linking to user query (from backend replyToMessageId)
            if (humanMessageId) {
              result.humanMessageId = humanMessageId;
            }

            // Carry forward the persisted feedback blob (set by the chat-v2
            // feedback endpoint and serialised on every messages-list row by
            // ``MessageItemResponse.feedback``). Without this, a bot message
            // that already has feedback loses it during sidebar
            // transformation, the downstream FeedbackModal sees ``feedback ==
            // null`` and falls back to the editable form — defeating the
            // write-once contract enforced by the backend.
            if ((msg as any).feedback) {
              (result as any).feedback = (msg as any).feedback;
            }

            logInfo('ConversationSidebar', 'Bot message with IDs for feedback and linking:', {
              botMessageId: result.botMessageId,
              humanMessageId: result.humanMessageId || 'none',
              usedBotMessageIdFromContent: !!botMessageIdFromContent,
              hasPersistedFeedback: !!(msg as any).feedback,
            });
          }
          
          // For bot messages, ALWAYS store the original message field (contains raw JSON).
          if (role === 'assistant' && msg.message) {
            (result as any).message = msg.message;
            logInfo('ConversationSidebar', 'Preserved message field for bot message:', {
              messagePreview: typeof msg.message === 'string' ? msg.message.substring(0, 100) : 'not string',
              isJSON: typeof msg.message === 'string' && msg.message.trim().startsWith('{'),
            });
            
            // Extract suggestedQueries from normal_text responses for history
            try {
              const parsedMsg = typeof msg.message === 'string' ? JSON.parse(msg.message) : msg.message;
              const suggested = parsedMsg?.suggested_queries ?? parsedMsg?.data?.suggested_queries;
              if (Array.isArray(suggested) && suggested.length > 0) {
                (result as any).suggestedQueries = suggested;
                logInfo('ConversationSidebar', 'Extracted suggestedQueries from bot message:', { count: suggested.length });
              }
            } catch {
              // Not JSON or no suggested queries
            }
          }
          
          return result;
        });

        console.log('[ConversationSidebar] ===== Final transformed messages =====', {
          totalMessages: transformedMessages.length,
          messages: transformedMessages.map((m, i) => ({
            index: i + 1,
            messageId: m.messageId,
            role: m.role,
            contentLength: m.content?.length || 0,
            hasContent: !!m.content,
            contentPreview: m.content?.substring(0, 100) || 'NO CONTENT',
            hasMessageField: !!(m as any).message,
            timestamp: m.timestamp,
          })),
          userMessages: transformedMessages.filter(m => m.role === 'user').length,
          botMessages: transformedMessages.filter(m => m.role === 'assistant').length,
        });
        
        logInfo('ConversationSidebar', `Loaded ${transformedMessages.length} messages`);
        const dataSourceName = conversation.dataSourceName;
        onSelectConversation(conversation.conversationId, transformedMessages, dataSourceName ? { dataSourceName } : undefined);
      } else {
        const errorMsg = response.error || 'Unknown error';
        logError('ConversationSidebar', `Failed to load messages - ${errorMsg}`, new Error(errorMsg));
        // Show error to user
        setError(`Failed to load messages: ${errorMsg}`);
        setSelectedConversationId(currentConversationId ?? null);
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 
                      err.response?.data?.message || 
                      err.message || 
                      'Failed to load conversation messages';
      logError('ConversationSidebar', `Failed to load conversation messages - ${errorMsg}`, err);
      // Show error to user
      setError(`Failed to load messages: ${errorMsg}`);
      setSelectedConversationId(currentConversationId ?? null);
    } finally {
      setLoadingMessages(null);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown date';
    
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      
      return date.toLocaleDateString();
    } catch {
      return 'Unknown date';
    }
  };

  const formatTime = (dateString?: string) => {
    if (!dateString) return null;
    
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    } catch {
      return null;
    }
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) {
      return 'Unknown';
    }

    try {
      const date = new Date(dateString);
      
      if (isNaN(date.getTime())) {
        console.error(`[formatDateTime] Invalid date: ${dateString}`);
        return 'Unknown';
      }

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      
      // If today, show time only
      if (dateOnly.getTime() === today.getTime()) {
        return date.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        });
      }
      
      // If this week, show day and time
      const diffDays = Math.floor((today.getTime() - dateOnly.getTime()) / 86400000);
      if (diffDays < 7) {
        const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
        return `${dayName} ${date.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        })}`;
      }
      
      // Otherwise show date and time
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error(`[formatDateTime] Error parsing date "${dateString}":`, error);
      return 'Unknown';
    }
  };

  const getConversationTitle = (conversation: Conversation) => {
    // First check for conversationTitle from the new SDK
    const convAny = conversation as any;
    if (convAny.conversationTitle && convAny.conversationTitle.trim()) {
      return convAny.conversationTitle.trim();
    }

    // Fallback to existing title field
    if (conversation.title) return conversation.title;

    // If no title, use start time as title
    const startTime = convAny.startedAt;
    if (startTime) {
      try {
        // Handle numeric timestamps (Unix timestamp in seconds)
        const timestamp = typeof startTime === 'number' ? startTime * 1000 : startTime;
        const date = new Date(timestamp);
        return date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
      } catch (error) {
        console.log(`[getConversationTitle] Error parsing start time: ${startTime}`, error);
      }
    }

    // Final fallback to lastMessage
    if (conversation.lastMessage) {
      // Try to parse if it's JSON
      let preview = conversation.lastMessage;
      try {
        const parsed = JSON.parse(conversation.lastMessage);
        if (parsed && typeof parsed === 'object') {
          if (parsed.content) {
            preview = parsed.content;
          } else if (parsed.type === 'complete' && parsed.data) {
            preview = 'Response with data';
          }
        }
      } catch (e) {
        // Not JSON, use as-is
      }
      
      return preview.length > 50 
        ? preview.substring(0, 50) + '...'
        : preview;
    }
    return `Conversation ${conversation.conversationId?.substring(0, 8) || 'Unknown'}`;
  };

  const getConversationPreview = (conversation: Conversation) => {
    if (!conversation.lastMessage) return null;
    
    // Try to parse if it's JSON
    let preview = conversation.lastMessage;
    try {
      const parsed = JSON.parse(conversation.lastMessage);
      if (parsed && typeof parsed === 'object') {
        if (parsed.content) {
          preview = parsed.content;
        } else if (parsed.type === 'complete' && parsed.data) {
          const data = parsed.data;
          let summary = 'Response with ';
          const parts: string[] = [];
          if (data.charts && Array.isArray(data.charts) && data.charts.length > 0) {
            parts.push(`${data.charts.length} chart${data.charts.length > 1 ? 's' : ''}`);
          }
          if (data.insights && Array.isArray(data.insights) && data.insights.length > 0) {
            parts.push(`${data.insights.length} insight${data.insights.length > 1 ? 's' : ''}`);
          }
          if (data.statistics) {
            parts.push('statistics');
          }
          if (parts.length > 0) {
            preview = summary + parts.join(', ');
          } else {
            preview = 'Structured response';
          }
        }
      }
    } catch (e) {
      // Not JSON, use as-is
    }
    
    return preview;
  };

  const downloadTextFile = (filename: string, content: string, mimeType = 'application/json;charset=utf-8') => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const safeFilename = (value: string) =>
    value
      .trim()
      .replace(/[^a-z0-9]+/gi, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 80) || 'conversation';

  const handleExportConversation = async (conversation: Conversation) => {
    if (!conversation.conversationId) return;
    setExportingConversationId(conversation.conversationId);

    try {
      const response = await ConversationService.getConversationMessages(conversation.conversationId);
      if (!response.success) {
        toastError('Export failed', response.error ?? 'Could not load this conversation for export.');
        return;
      }

      const title = getConversationTitle(conversation);
      const payload = {
        exportedAt: new Date().toISOString(),
        conversation: {
          id: conversation.conversationId,
          title,
          createdAt: conversation.createdAt,
          startedAt: conversation.startedAt,
          updatedAt: conversation.updatedAt,
          endedAt: conversation.endedAt,
          messageCount: conversation.messageCount,
          dataSourceName: conversation.dataSourceName,
        },
        messages: response.messages,
      };

      downloadTextFile(
        `${safeFilename(title)}_${conversation.conversationId.slice(0, 8)}.json`,
        JSON.stringify(payload, null, 2)
      );
      toastSuccess('Conversation downloaded', 'The conversation history has been exported.');
    } catch (err) {
      logError('ConversationSidebar', 'Failed to export conversation', err instanceof Error ? err : new Error('Unknown error'));
      toastError('Export failed', 'Could not export this conversation. Please try again.');
    } finally {
      setExportingConversationId(null);
    }
  };

  const beginRenameConversation = (conversation: Conversation) => {
    if (!conversation.conversationId) return;
    setOpenActionsConversationId(null);
    setEditingConversationId(conversation.conversationId);
    setEditingConversationTitle(getConversationTitle(conversation));
  };

  const cancelRenameConversation = () => {
    setEditingConversationId(null);
    setEditingConversationTitle('');
  };

  const submitRenameConversation = async (conversation: Conversation) => {
    if (!conversation.conversationId) return;
    const trimmed = editingConversationTitle.trim();
    if (!trimmed) {
      cancelRenameConversation();
      return;
    }
    setRenamingConversationId(conversation.conversationId);
    try {
      const result = await ConversationService.renameConversation(conversation.conversationId, trimmed);
      if (result.success) {
        const nextTitle = result.title ?? trimmed;
        const nextUpdatedAt = result.updatedAt ?? new Date().toISOString();
        setConversations((prev) =>
          prev.map((item) =>
            item.conversationId === conversation.conversationId
              ? {
                  ...item,
                  title: nextTitle,
                  updatedAt: nextUpdatedAt,
                  endedAt: nextUpdatedAt,
                }
              : item
          )
        );
        toastSuccess('Thread renamed', 'The thread title has been updated.');
        cancelRenameConversation();
        if (isFinanceV2Variant) {
          await loadConversations();
        }
      } else {
        toastError('Rename failed', result.error ?? 'Could not rename this thread.');
      }
    } catch (err) {
      logError('ConversationSidebar', 'Failed to rename conversation', err instanceof Error ? err : new Error('Unknown error'));
      toastError('Rename failed', 'Could not rename this thread. Please try again.');
    } finally {
      setRenamingConversationId(null);
    }
  };

  const handleDeleteConversation = async (conversation: Conversation) => {
    if (!conversation.conversationId) return;
    setOpenActionsConversationId(null);
    const confirmed = window.confirm(
      'Are you sure you want to delete this conversation? This action cannot be undone.'
    );
    if (!confirmed) return;
    setDeletingConversationId(conversation.conversationId);
    try {
      const result = await ConversationService.deleteConversation(conversation.conversationId);
      if (result.success) {
        setConversations((prev) => prev.filter((c) => c.conversationId !== conversation.conversationId));
        if (currentConversationId === conversation.conversationId) {
          onStartNewConversation?.();
        }
        toastSuccess('Conversation deleted', 'The conversation has been removed.');
        if (isFinanceV2Variant) {
          await loadConversations();
        }
      } else {
        toastError('Could not delete', result.error ?? 'Failed to delete conversation. Please try again.');
      }
    } catch (err) {
      logError('ConversationSidebar', 'Failed to delete conversation', err instanceof Error ? err : new Error('Unknown error'));
      toastError('Could not delete', 'Failed to delete conversation. Please try again.');
    } finally {
      setDeletingConversationId(null);
    }
  };

  const handleToggleFavouriteConversation = async (conversation: Conversation, isBookmarked: boolean) => {
    if (!conversation.conversationId) return;
    setOpenActionsConversationId(null);
    setSavingBookmarkConversationId(conversation.conversationId);

    try {
      if (isFinanceV2Variant) {
        const nextFavourite = !isBookmarked;
        const result = await ConversationService.setConversationFavourite(
          conversation.conversationId,
          nextFavourite
        );
        if (result.success) {
          const updatedAt = result.updatedAt ?? conversation.updatedAt ?? new Date().toISOString();
          setConversations((prev) =>
            prev.map((item) =>
              item.conversationId === conversation.conversationId
                ? {
                    ...item,
                    isFavourite: result.isFavourite ?? nextFavourite,
                    updatedAt,
                    endedAt: updatedAt,
                  }
                : item
            )
          );
          toastSuccess(
            nextFavourite ? 'Saved' : 'Removed',
            nextFavourite ? 'This thread has been added to favourites.' : 'This thread has been removed from favourites.'
          );
          await loadConversations();
        } else {
          toastError('Favourite failed', result.error ?? 'Could not update this favourite.');
        }
        return;
      }

      if (!isBookmarked) {
        const saved = await ConversationService.saveConversationBookmark(
          conversation.conversationId,
          getConversationTitle(conversation)
        );
        if (saved) {
          setBookmarksResponse((prev) => prev
            ? {
                ...prev,
                bookmarks: [
                  saved,
                  ...prev.bookmarks.filter((bm) => bm.conversationId !== conversation.conversationId),
                ],
              }
            : { success: true, bookmarks: [saved] });
          toastSuccess('Saved', 'This thread has been added to favourites.');
        } else {
          toastError('Could not save', 'Favourite could not be saved for this thread.');
        }
        return;
      }

      const existing = bookmarksResponse?.bookmarks?.find(
        (bm) => bm.conversationId === conversation.conversationId
      );
      if (!existing) return;

      const removed = await ConversationService.deleteConversationBookmark(existing.id);
      if (removed) {
        setBookmarksResponse((prev) => prev
          ? { ...prev, bookmarks: prev.bookmarks.filter((bm) => bm.id !== existing.id) }
          : prev);
        toastSuccess('Removed', 'This thread has been removed from favourites.');
      } else {
        toastError('Could not remove', 'Favourite could not be removed for this thread.');
      }
    } catch (err) {
      console.error('[ConversationSidebar] Failed to toggle bookmark', err);
      toastError('Favourite failed', 'Could not update this favourite. Please try again.');
    } finally {
      setSavingBookmarkConversationId(null);
    }
  };

  const searchInputText = searchQuery.trim();
  const canSubmitFinanceV2Search = searchInputText.length >= 2;
  const submitFinanceV2Search = () => {
    if (!isFinanceV2Variant || !canSubmitFinanceV2Search || loading) return;
    setSubmittedSearchQuery(searchInputText);
  };
  const handleFinanceV2SearchChange = (value: string) => {
    setSearchQuery(value);
    if (value.trim().length === 0 && submittedSearchQuery) {
      setSubmittedSearchQuery('');
    }
  };
  const clearFinanceV2Search = () => {
    setSearchQuery('');
    if (submittedSearchQuery) {
      setSubmittedSearchQuery('');
    }
  };

  // Filter conversations by search query and Finance V2 history filter
  const filteredConversations = conversations.filter((conv) => {
    if (isFinanceV2Variant) return true;
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;
    const title = getConversationTitle(conv).toLowerCase();
    const preview = getConversationPreview(conv)?.toLowerCase() || '';
    return title.includes(query) || preview.includes(query);
  });
  const threadSummary = `${conversations.length} ${conversations.length === 1 ? 'conversation' : 'conversations'}`;
  // Keep the previous FB2 section label behind a flag for quick rollback.
  const showFinanceV2SectionLabel = false;
  const showFinanceV2ConversationIcon = false;

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        "h-full flex flex-col relative border-r border-gray-200",
        isFinanceV2Variant
          ? "border-r-0 bg-[#fbf8f4]"
          : "bg-gradient-to-br from-gray-50 to-white shadow-lg"
      )}
    >
      {/* Header */}
      <div
        className={cn(
          "sticky top-0 z-20 bg-white border-b",
          isFinanceV2Variant ? "border-[#EDE6E1] px-4 py-3" : "border-gray-200 px-4 py-3"
        )}
      >
        <div className={cn("flex items-start justify-between", isFinanceV2Variant ? "gap-4" : "mb-3")}>
          <div className="min-w-0">
            {isFinanceV2Variant ? (
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] bg-[#F5E9EE] text-[#8B2D60]">
                  <History className="h-[18px] w-[18px]" />
                </div>
                <div className="min-w-0">
                  <h2 className="font-display text-[16px] font-semibold leading-tight text-[#1F1A20]">
                    Chat History
                  </h2>
                  <p className="mt-1 text-[12.5px] leading-tight text-[#7B6B72]">
                    {threadSummary}
                  </p>
                </div>
              </div>
            ) : (
              <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <MessageSquare className="h-5 w-5 text-primary-600" />
                Conversations
              </h2>
            )}
          </div>
          <div className={cn("flex gap-2", isFinanceV2Variant ? "items-start" : "items-center")}>
            {/* New Conversation Button */}
            {!isFinanceV2Variant && (
              <button
                onClick={() => {
                  if (onStartNewConversation) {
                    onStartNewConversation();
                    return;
                  }
                  console.warn('[ConversationSidebar] onStartNewConversation is not provided');
                }}
                disabled={!onStartNewConversation}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-primary hover:bg-primary/90 transition-colors text-primary-foreground font-medium text-sm shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                title="Start new conversation"
              >
                <Plus className="h-4 w-4" />
                <span>New</span>
              </button>
            )}
            <button
              onClick={onClose}
              className={cn(
                "transition-colors text-gray-500 hover:text-gray-700",
                isFinanceV2Variant
                  ? "flex h-8 w-8 items-center justify-center rounded-full bg-transparent text-[#7B6B72] hover:bg-[#FAF7F1] hover:text-[#1F1A20]"
                  : "p-1.5 rounded-lg hover:bg-gray-100"
              )}
              title="Close sidebar"
            >
              <X className={cn(isFinanceV2Variant ? "h-5 w-5" : "h-4 w-4")} />
            </button>
          </div>
        </div>
        
        {/* Search and compact filter */}
        {isFinanceV2Variant ? (
          <div className="mt-5 flex h-9 items-center overflow-hidden rounded-[8px] border border-[#EDE6E1] bg-white">
            <div className="relative min-w-0 flex-1">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#7B6B72]" />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => handleFinanceV2SearchChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  submitFinanceV2Search();
                }}
                className="h-10 w-full border-0 bg-transparent py-0 pl-10 pr-9 text-[12.5px] text-[#1F1A20] outline-none placeholder:text-[#A8989F] focus:outline-none focus:ring-0"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={clearFinanceV2Search}
                  className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-[#7B6B72] transition-colors hover:bg-[#FAF7F1] hover:text-[#1F1A20]"
                  title="Clear search"
                  aria-label="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <button
              type="button"
              disabled={!canSubmitFinanceV2Search || loading}
              onClick={submitFinanceV2Search}
              className={cn(
                "flex h-10 w-10 shrink-0 items-center justify-center border-l border-[#EDE6E1] transition-colors disabled:cursor-not-allowed disabled:opacity-45",
                canSubmitFinanceV2Search
                  ? "text-[#6E3450] hover:bg-[#FAF7F1]"
                  : "text-[#A8989F]"
              )}
              title={canSubmitFinanceV2Search ? "Search conversations" : "Enter at least 2 characters"}
              aria-label="Search conversations"
            >
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <div className="relative">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400"
              />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-10 pr-4 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div
        className={cn("flex-1 overflow-y-auto", isFinanceV2Variant && "bg-[#fbf8f4]")}
        onScroll={isFinanceV2Variant ? handleConversationScroll : undefined}
        data-finance-v2-history-scroll={isFinanceV2Variant ? "true" : undefined}
      >
        {loading && conversations.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div
                className={cn(
                  "mx-auto mb-3 h-10 w-10 animate-spin rounded-full border-4",
                  isFinanceV2Variant
                    ? "border-[#e7c8d2] border-t-[#6E3450]"
                    : "border-primary-200 border-t-primary-600"
                )}
              />
              <p className={cn("text-sm font-medium", isFinanceV2Variant ? "text-[#7B6B72]" : "text-gray-500")}>
                Loading conversations...
              </p>
            </div>
          </div>
        ) : error ? (
          <div className="p-4">
            <div className={cn("rounded-lg border p-4", isFinanceV2Variant ? "border-[#e4bac3] bg-[#f5e9ee]" : "border-red-200 bg-red-50")}>
              <p className={cn("mb-2 text-sm font-medium", isFinanceV2Variant ? "text-[#B23A48]" : "text-red-800")}>{error}</p>
              <button
                onClick={loadConversations}
                className={cn("text-sm font-medium underline", isFinanceV2Variant ? "text-[#6E3450] hover:text-[#5A2C58]" : "text-red-600 hover:text-red-800")}
              >
                Try again
              </button>
            </div>
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center p-6">
              <MessageSquare className={cn("mx-auto mb-4 h-16 w-16", isFinanceV2Variant ? "text-[#D9CFC4]" : "text-gray-300")} />
              <p className={cn("mb-1 text-sm font-medium", isFinanceV2Variant ? "text-[#7B6B72]" : "text-gray-500")}>
                {(isFinanceV2Variant ? submittedSearchQuery : searchQuery.trim())
                    ? 'No conversations found'
                    : 'No conversations yet'}
              </p>
              <p className={cn("text-xs", isFinanceV2Variant ? "text-[#A8989F]" : "text-gray-400")}>
                {(isFinanceV2Variant ? submittedSearchQuery : searchQuery.trim())
                    ? 'Try a different search term'
                    : 'Start chatting to see your history'}
              </p>
            </div>
          </div>
        ) : (
          <div className={cn(isFinanceV2Variant ? "p-0" : "divide-y divide-gray-100")}>
            {isFinanceV2Variant && showFinanceV2SectionLabel && (
              <div className="px-4 pb-2 pt-4">
                <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#7B6B72]">
                  RECENT
                </span>
              </div>
            )}
            {filteredConversations.map((conversation) => {
              const isActive = conversation.conversationId === selectedConversationId;
              const isLoading = loadingMessages === conversation.conversationId;
              const preview = getConversationPreview(conversation);
              const isBookmarked = isFinanceV2Variant
                ? Boolean(conversation.isFavourite)
                : !!bookmarksResponse?.bookmarks?.some(
                    (bm) => bm.conversationId === conversation.conversationId
                  );
              const messageCount = conversation.messageCount ?? 0;
              const isDraft = isFinanceV2Variant && messageCount === 0;
              const isEditingTitle = editingConversationId === conversation.conversationId;
              const isRenaming = renamingConversationId === conversation.conversationId;
              const isActionsOpen = openActionsConversationId === conversation.conversationId;
              const favouriteTooltipText = isBookmarked ? "Remove from favourites" : "Add to favourites";
              
              // Extract date fields - prioritize startedAt from the JSON data (API returns startedAt/endedAt)
              const convAny = conversation as any;
              const startedAt = convAny.startedAt; // Primary time field from JSON (API uses startedAt)
              const endedAt = convAny.endedAt; // API uses endedAt
              const updatedAt = convAny.updatedAt || convAny.updated_at || convAny.lastUpdated || convAny.lastUpdatedAt || convAny.modifiedAt;
              const createdAt = convAny.createdAt || convAny.created_at || convAny.created || convAny.creationTime || convAny.creationTimestamp;

              // Only log errors for missing startedAt (not every conversation)
              if (!startedAt && !updatedAt && !createdAt) {
                console.warn(`[ConversationSidebar] ⚠️ No timestamp found for conversation ${conversation.conversationId}`);
              }
              
              return (
                <div
                  key={conversation.conversationId}
                  role="button"
                  tabIndex={0}
                  onClick={() => !isLoading && !isEditingTitle && handleConversationClick(conversation)}
                  onKeyDown={(e) => {
                    if (!isLoading && !isEditingTitle && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      handleConversationClick(conversation);
                    }
                  }}
                  className={cn(
                    "w-full text-left transition-all duration-200",
                    isFinanceV2Variant
                      ? "group relative overflow-visible border-b border-l-2 border-b-[#EDE6E1] border-l-transparent px-4 py-2 hover:bg-[#FAF7F1]"
                      : "p-4 hover:bg-gray-50",
                    isActive && (
                      isFinanceV2Variant
                        ? "border-l-[#6E3450] bg-[#f5e9ee]"
                        : "bg-gradient-to-r from-primary-50 to-primary-100/50 border-l-4 border-primary-600 shadow-sm"
                    ),
                    !isActive && !isFinanceV2Variant && "hover:border-l-4 hover:border-gray-300",
                    isLoading && "opacity-60 cursor-wait"
                  )}
                >
                  <div className={cn("flex items-start justify-between gap-2", isFinanceV2Variant && "items-center", isFinanceV2Variant && isEditingTitle && "gap-2", !isFinanceV2Variant && "mb-2")}>
                    <div className="min-w-0 flex-1">
                      {isEditingTitle ? (
                        <div
                          className={cn("flex min-w-0 items-center", isFinanceV2Variant ? "gap-2" : "gap-1.5")}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            ref={renameInputRef}
                            value={editingConversationTitle}
                            onChange={(e) => setEditingConversationTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                submitRenameConversation(conversation);
                              }
                              if (e.key === 'Escape') {
                                e.preventDefault();
                                cancelRenameConversation();
                              }
                            }}
                            disabled={isRenaming}
                            className={cn(
                              "min-w-0 flex-1 appearance-none bg-white font-display text-[#1F1A20] outline-none ring-0 disabled:opacity-70 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0",
                              isFinanceV2Variant
                                ? "h-9 rounded-[8px] border border-[#8B2D60] px-2.5 text-[13px] font-medium leading-none shadow-none focus:border-[#8B2D60] focus:shadow-none focus-visible:shadow-none"
                                : "rounded-[8px] border border-[#D7C8CE] px-2 py-1 text-[13px] font-semibold leading-tight focus:border-[#6E3450]"
                            )}
                            aria-label="Thread title"
                          />
                          <button
                            type="button"
                            className={cn(
                              "flex items-center justify-center rounded-full text-[#7B6B72] hover:text-[#1F1A20]",
                              isFinanceV2Variant
                                ? "h-6 w-6 shrink-0 border border-[#EDE6E1] bg-white shadow-[0_1px_2px_rgba(31,26,32,.08)] hover:bg-[#FAF7F1]"
                                : "h-7 w-7 hover:bg-white"
                            )}
                            title="Cancel rename"
                            aria-label="Cancel rename"
                            onClick={(e) => {
                              e.stopPropagation();
                              cancelRenameConversation();
                            }}
                          >
                            <X
                              className={cn(isFinanceV2Variant ? "h-4 w-4" : "h-3.5 w-3.5")}
                            />
                          </button>
                          <button
                            type="button"
                            className={cn(
                              "flex items-center justify-center rounded-full disabled:cursor-not-allowed disabled:opacity-70",
                              isFinanceV2Variant
                                ? "h-6 w-6 shrink-0 bg-[#8B2D60] text-white shadow-[0_8px_18px_-14px_rgba(110,52,80,.8)] hover:bg-[#6E3450]"
                                : "h-7 w-7 text-[#3F7D4F] hover:bg-white"
                            )}
                            title="Save title"
                            aria-label="Save title"
                            disabled={isRenaming}
                            onClick={(e) => {
                              e.stopPropagation();
                              submitRenameConversation(conversation);
                            }}
                          >
                            {isRenaming ? (
                              <div
                                className={cn(
                                  "animate-spin rounded-full border-2 border-t-transparent",
                                  isFinanceV2Variant ? "h-4 w-4 border-white" : "h-3.5 w-3.5 border-[#3F7D4F]"
                                )}
                              />
                            ) : (
                              <Check
                                className={cn(isFinanceV2Variant ? "h-4 w-4" : "h-3.5 w-3.5")}
                              />
                            )}
                          </button>
                        </div>
	                      ) : (
	                        <div className="flex min-w-0 items-center gap-2">
		                          {isFinanceV2Variant && showFinanceV2ConversationIcon && (
		                            <span
		                              className="flex h-4 w-4 shrink-0 items-center justify-center text-[#A8989F]"
		                              aria-hidden="true"
	                            >
	                              <MessageSquareText className="h-3.5 w-3.5" />
	                            </span>
	                          )}
                          <h3
                            title={getConversationTitle(conversation)}
                            className={cn(
                              "truncate font-semibold",
                              isFinanceV2Variant ? "font-display text-[13px] leading-5" : "mb-1 text-sm",
                              isFinanceV2Variant
                                ? isActive
                                  ? "text-[#5A2C58]"
                                  : isDraft
                                    ? "italic text-[#7B6B72]"
                                    : "text-[#1F1A20]"
                                : isActive ? "text-primary-900" : "text-gray-900"
                            )}
                          >
                            {getConversationTitle(conversation)}
                          </h3>
                          {isDraft && (
                            <span className="shrink-0 rounded border border-[#EDE6E1] bg-white/40 px-1.5 py-[1px] text-[9.5px] uppercase tracking-[0.18em] text-[#A8989F]">
                              Draft
                            </span>
                          )}
                        </div>
                      )}
                    </div>
	                    {!(isFinanceV2Variant && isEditingTitle) && (
	                    <div className={cn("flex shrink-0 items-center", isFinanceV2Variant ? "gap-1" : "gap-2")}>
	                      {isFinanceV2Variant ? (
	                        <>
		                          <TooltipProvider delayDuration={250}>
		                            <Tooltip>
		                              <TooltipTrigger asChild>
		                                <button
		                                  type="button"
		                                  disabled={savingBookmarkConversationId === conversation.conversationId}
		                                  className={cn(
		                                    "flex h-6 w-6 items-center justify-center rounded-full opacity-80 transition-colors hover:bg-white group-hover:opacity-100 disabled:pointer-events-none disabled:opacity-70",
		                                    isBookmarked ? "text-[#8B2D60]" : "text-[#A8989F] hover:text-[#6E3450]"
		                                  )}
		                                  aria-label={favouriteTooltipText}
		                                  onClick={(e) => {
		                                    e.stopPropagation();
		                                    handleToggleFavouriteConversation(conversation, isBookmarked);
		                                  }}
		                                >
		                                  {savingBookmarkConversationId === conversation.conversationId ? (
		                                    <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#6E3450] border-t-transparent" />
		                                  ) : (
		                                    <Star className={cn("h-4 w-4", isBookmarked && "fill-current")} />
		                                  )}
		                                </button>
		                              </TooltipTrigger>
		                              <TooltipContent side="left" align="center" className="text-[11px]">
		                                {favouriteTooltipText}
		                              </TooltipContent>
		                            </Tooltip>
		                          </TooltipProvider>
	                          <div className="relative">
	                            <button
	                              type="button"
	                              className="flex h-6 w-6 items-center justify-center rounded-full text-[#A8989F] opacity-80 transition-colors hover:bg-white hover:text-[#1F1A20] group-hover:opacity-100"
	                              title="Thread actions"
	                              aria-label="Thread actions"
	                              aria-expanded={isActionsOpen}
	                              onClick={(e) => {
	                                e.stopPropagation();
	                                if (openActionsConversationId === conversation.conversationId) {
	                                  setOpenActionsConversationId(null);
	                                  return;
	                                }
	                                const buttonRect = e.currentTarget.getBoundingClientRect();
	                                const scrollContainer = e.currentTarget.closest('[data-finance-v2-history-scroll="true"]');
	                                const scrollRect = scrollContainer?.getBoundingClientRect();
	                                const visibleTop = scrollRect?.top ?? 0;
	                                const visibleBottom = scrollRect?.bottom ?? window.innerHeight;
	                                const menuHeight = 132;
	                                const spaceBelow = visibleBottom - buttonRect.bottom;
	                                const spaceAbove = buttonRect.top - visibleTop;
	                                setActionsMenuPlacement(spaceBelow < menuHeight && spaceAbove > spaceBelow ? 'up' : 'down');
	                                setOpenActionsConversationId(conversation.conversationId);
	                              }}
	                            >
	                              <MoreHorizontal className="h-4 w-4 rotate-90" />
	                            </button>
	                          {isActionsOpen && (
	                            <div
	                              className={cn(
	                                "absolute right-0 z-50 w-48 rounded-[10px] border border-[#EDE6E1] bg-white py-1.5 text-[12.5px] shadow-[0_12px_28px_-12px_rgba(31,26,32,.28)]",
	                                actionsMenuPlacement === 'up' ? "bottom-8" : "top-8"
	                              )}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                className="flex w-full items-center gap-2.5 px-3 py-2 text-left font-medium text-[#1F1A20] hover:bg-[#FAF7F1]"
                                onClick={() => beginRenameConversation(conversation)}
                              >
                                <Pencil className="h-3.5 w-3.5 text-[#7B6B72]" />
                                Rename
                              </button>
                              <button
                                type="button"
                                disabled={exportingConversationId === conversation.conversationId}
                                className="flex w-full items-center gap-2.5 px-3 py-2 text-left font-medium text-[#1F1A20] hover:bg-[#FAF7F1] disabled:opacity-70"
                                onClick={() => {
                                  setOpenActionsConversationId(null);
                                  handleExportConversation(conversation);
                                }}
                              >
                                <Download className="h-3.5 w-3.5 text-[#7B6B72]" />
                                Download
                              </button>
                              <button
                                type="button"
                                disabled={deletingConversationId === conversation.conversationId}
                                className="flex w-full items-center gap-2.5 px-3 py-2 text-left font-medium text-[#B23A48] hover:bg-[#FAF7F1] disabled:opacity-70"
                                onClick={() => handleDeleteConversation(conversation)}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                                Delete
	                              </button>
	                            </div>
	                          )}
	                          </div>
	                        </>
	                      ) : (
                        <>
                          <button
                            type="button"
                            disabled={savingBookmarkConversationId === conversation.conversationId}
                            className={cn(
                              "rounded-full disabled:opacity-70 disabled:pointer-events-none flex items-center justify-center transition-colors",
                              isBookmarked
                                ? "p-1 border border-primary-400 bg-primary/10 text-primary"
                                : "p-1 border border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                            )}
                            title={isBookmarked ? 'Favourite conversation' : 'Mark as favourite'}
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!conversation.conversationId) return;
                              setSavingBookmarkConversationId(conversation.conversationId);
                              try {
                                if (!isBookmarked) {
                                  const saved = await ConversationService.saveConversationBookmark(
                                    conversation.conversationId,
                                    getConversationTitle(conversation)
                                  );
                                  if (saved) {
                                    setBookmarksResponse((prev) => prev
                                      ? { ...prev, bookmarks: [saved, ...prev.bookmarks] }
                                      : { success: true, bookmarks: [saved] });
                                    toastSuccess('Saved', 'This conversation is saved. You can see it on the top header.');
                                  }
                                } else if (bookmarksResponse?.bookmarks) {
                                  const existing = bookmarksResponse.bookmarks.find(
                                    (bm) => bm.conversationId === conversation.conversationId
                                  );
                                  if (existing && await ConversationService.deleteConversationBookmark(existing.id)) {
                                    setBookmarksResponse((prev) => prev
                                      ? { ...prev, bookmarks: prev.bookmarks.filter((bm) => bm.id !== existing.id) }
                                      : prev);
                                  }
                                }
                              } catch (err) {
                                console.error('[ConversationSidebar] Failed to toggle bookmark', err);
                              } finally {
                                setSavingBookmarkConversationId(null);
                              }
                            }}
                          >
                            {savingBookmarkConversationId === conversation.conversationId ? (
                              <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#6E3450] border-t-transparent" />
                            ) : (
                              <Star className={cn("h-3.5 w-3.5", isBookmarked && "fill-current")} />
                            )}
                          </button>
                          <button
                            type="button"
                            disabled={deletingConversationId === conversation.conversationId}
                            className="rounded-full text-gray-400 hover:text-red-600 disabled:opacity-70 disabled:pointer-events-none flex items-center justify-center transition-colors p-1 border border-gray-200 hover:bg-red-50 hover:border-red-200"
                            title="Delete conversation"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteConversation(conversation);
                            }}
                          >
                            {deletingConversationId === conversation.conversationId ? (
                              <div className="h-3.5 w-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Trash2 className="h-3.5 w-3.5" />
                            )}
                          </button>
                        </>
                      )}
                      {isLoading && (
                        <div className={cn("h-4 w-4 flex-shrink-0 animate-spin rounded-full border-2 border-t-transparent", isFinanceV2Variant ? "border-[#6E3450]" : "border-primary-600")} />
                      )}
                    </div>
                    )}
                  </div>
                  
                  {preview && !isFinanceV2Variant && (
                    <p className="text-xs text-gray-600 line-clamp-2 mb-2 leading-relaxed">
                      {preview.length > 80
                        ? preview.substring(0, 80) + '...'
                        : preview}
                    </p>
                  )}
                  
                  {/* Time display - use startedAt time from conversation JSON */}
                  {!(isFinanceV2Variant && isEditingTitle) && (
                  <div className={cn("flex items-center justify-between", isFinanceV2Variant ? "mt-1" : "mt-2")}>
                    {(() => {
                      // Handle numeric timestamps (convert to Date objects)
                      let displayTime = 'No time available';

                      if (startedAt) {
                        try {
                          // Handle numeric timestamps (Unix timestamp in seconds)
                          // Check if it's already in milliseconds (timestamp > year 2000 in seconds)
                          let timestamp: number;
                          if (typeof startedAt === 'number') {
                            // If timestamp is less than year 2000 in seconds, it's in seconds, multiply by 1000
                            // Year 2000 in seconds: 946684800
                            // If timestamp > 946684800000, it's already in milliseconds
                            if (startedAt < 946684800) {
                              // Timestamp is in seconds, convert to milliseconds
                              timestamp = startedAt * 1000;
                            } else if (startedAt > 946684800000) {
                              // Already in milliseconds
                              timestamp = startedAt;
                            } else {
                              // Between 946684800 and 946684800000 - likely seconds, convert to milliseconds
                              timestamp = startedAt * 1000;
                            }
                          } else {
                            // String timestamp, try to parse
                            timestamp = new Date(startedAt).getTime();
                          }
                          const date = new Date(timestamp);
                          if (!isNaN(date.getTime())) {
                            displayTime = isFinanceV2Variant
                              ? formatDate(date.toISOString())
                              : formatDateTime(date.toISOString());
                          } else {
                            console.error(`[ConversationSidebar] Invalid date from timestamp: ${startedAt}`);
                            displayTime = 'Invalid date';
                          }
                        } catch (error) {
                          console.error(`[ConversationSidebar] Error parsing startedAt timestamp: ${startedAt}`, error);
                          displayTime = 'Invalid date';
                        }
                      } else if (updatedAt || createdAt) {
                        try {
                          const fallbackTimestamp = updatedAt || createdAt;
                          let timestamp: number;
                          if (typeof fallbackTimestamp === 'number') {
                            if (fallbackTimestamp < 946684800) {
                              timestamp = fallbackTimestamp * 1000;
                            } else if (fallbackTimestamp > 946684800000) {
                              timestamp = fallbackTimestamp;
                            } else {
                              timestamp = fallbackTimestamp * 1000;
                            }
                          } else {
                            timestamp = new Date(fallbackTimestamp).getTime();
                          }
                          const date = new Date(timestamp);
                          if (!isNaN(date.getTime())) {
                            displayTime = formatDate(date.toISOString());
                          }
                        } catch (error) {
                          console.error(`[ConversationSidebar] Error parsing fallback timestamp: ${updatedAt || createdAt}`, error);
                        }
                      }

                      return (
                        <div
                          className={cn(
                            "flex items-center gap-1.5",
                            isFinanceV2Variant
                              ? "text-[11px] italic text-[#7B6B72]"
                              : "text-sm text-primary-600"
                          )}
                        >
                          {!isFinanceV2Variant && <Clock className="h-4 w-4" />}
                          <span className={cn(!isFinanceV2Variant && "font-medium")}>
                            {displayTime}
                          </span>
                        </div>
                      );
                    })()}
                    {!isFinanceV2Variant && conversation.messageCount !== undefined && (
                      <span
                        className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700"
                      >
                        {conversation.messageCount} {conversation.messageCount === 1 ? 'msg' : 'msgs'}
                      </span>
                    )}
                  </div>
                  )}
                </div>
              );
            })}
            {isFinanceV2Variant && loadingMoreConversations && (
              <div className="flex items-center justify-center gap-2 py-3 text-[11px] font-medium text-[#7B6B72]">
                <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#E7C8D2] border-t-[#6E3450]" />
                <span>Loading more conversations...</span>
              </div>
            )}
	          </div>
	        )}
      </div>
    </div>
  );
}
